import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { search as searchApi } from "@/lib/api";
import { TYPES, linkTo, getTitle, getBlurb } from "@/config";

export function SearchDialog({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(""); setResults([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const data = await searchApi(q.trim());
        setResults(data.results || []);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(id);
    // Debounced search keyed on query; other refs are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  if (!open) return null;

  const go = (r) => { onClose(); navigate(linkTo(r._type, r.slug)); };
  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) { onClose(); navigate(`/search?q=${encodeURIComponent(q.trim())}`); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-ink/90 backdrop-blur-md" data-testid="search-dialog" onClick={onClose}>
      <div className="mx-auto max-w-3xl px-5 pt-24" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit} className="flex items-center gap-4 border-b border-gold/40 pb-4">
          <Search size={22} className="text-gold shrink-0" />
          <input
            ref={inputRef}
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search cases, people, artworks, museums…"
            className="w-full bg-transparent font-serif text-3xl text-parchment placeholder:text-faint/60 focus:outline-none"
          />
          <button type="button" onClick={onClose} aria-label="Close" className="text-faint hover:text-gold">
            <X size={22} />
          </button>
        </form>

        <div className="mt-6 max-h-[60vh] overflow-y-auto" data-testid="search-results">
          {loading && <p className="eyebrow py-6">Searching the archive…</p>}
          {!loading && q.trim().length >= 2 && results.length === 0 && (
            <p className="py-6 font-serif text-xl text-faint">No entries found for “{q}”.</p>
          )}
          {results.map((r) => (
            <button
              key={`${r._type}-${r.id}`}
              data-testid={`search-result-${r.slug}`}
              onClick={() => go(r)}
              className="group flex w-full items-center gap-4 border-b border-line py-4 text-left hover:pl-2 transition-all"
            >
              <span className="eyebrow w-24 shrink-0 text-gold/70">{TYPES[r._type]?.singular}</span>
              <div className="min-w-0">
                <div className="font-serif text-xl text-parchment group-hover:text-gold truncate">{getTitle(r)}</div>
                <div className="text-sm text-faint truncate">{getBlurb(r)}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
